/**
 * Q&Aダイアログコンポーネント
 * 質問一覧表示、回答入力、回答確認機能を提供
 */
"use client";
import React, { useState, useEffect } from "react";
import { DialogContent, IconButton, Alert, CircularProgress, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import providers from "../../../index.js";
import {useWebAppContext} from "../../../providers/WebAppProvider.js";
import dayjs  from "@metrojs/dayjs";
import logjs from  "@metrojs/logjs";
import Consts from "../../../../config/consts.js";

const log = new logjs("QaDialog");

//const { useWebAppContext } = providers;
import { useQaDialog } from "./useQaDialog.js";
import s from "./styles.js";

/**
 * Q&Aダイアログコンポーネント
 * 質問一覧・未回答一覧の表示、回答入力、確認、送信を行う。
 * @param {Object} props コンポーネントプロパティ
 * @param {boolean} props.open ダイアログ開閉状態
 * @param {Function} props.onClose ダイアログを閉じるコールバック
 * @param {Object} [props.params] 初期表示用パラメータ
 * @param {string} [props.params.MitsumoriNo] 見積番号
 * @param {string} [props.params.CarName]
 * @param {string} [props.params.Sharyo_Katashiki]
 * @param {string} [props.params.Sharyo_Nenshiki]
 * @param {string} [props.params.buhinId] 部品ID
 * @returns {JSX.Element} Q&Aダイアログ
 */
export default function QaDialog({ open, onClose, params = {} }) {
  log.debug("Start QaDialog params", params);
  const { MitsumoriNo, request = {}, anser = [], buhinId = null, isQuestion = true } = params;
  const { CarName, Sharyo_Katashiki, Sharyo_Nenshiki, RAID, Request_Limit } = request;
  const { state, actions, params: contextParams, searchParams: contextSearchParams } = useWebAppContext();

  const initState = {
    MitsumoriNo: MitsumoriNo,
    request: request,
    anser: anser,
    buhinId: buhinId,
    QAnaiyo: "", // 回答内容
  };
  const [formData, formProps] = useQaDialog(initState, state, actions);
  const { form, qaData, partsData, mikaitoList, qaList, qaCnt, isAnswerOpen, isShowConfirm, isShowFinish, isShowInit, isShowMikaitoList, selectedQa, loading, } = formData;

    //const [answerText, setAnswerText] = useState("");
    const [error, setError] = useState("");

  // ダイアログが開いた時の処理
  useEffect(() => {
    if (open && MitsumoriNo) {
      log.debug("Start QaDialog useEffect open MitsumoriNo", MitsumoriNo);
      formProps.init(MitsumoriNo, buhinId);
    }
  }, [open]);

  /**
   * ダイアログを閉じる処理
   * 入力内容と選択状態をリセットして閉じる。
   * @returns {void}
   */
  const handleClose = () => {
    setError("");
    onClose();
  };

  /**
   * 質問選択時の処理
   * @param {Object} qa - 選択されたQ&Aデータ
   */
  const handleSelectQa = (qa) => {
    formProps.selectQa(qa);
    formProps.setForm({...form, QAnaiyo: ""});
    setError("");
  }

  // 掲載期限（Request_Limit + 1週間）判定
  const canAsk = (() => {
    if (!Request_Limit) return true;
    return dayjs(Request_Limit).add(1, "week").isAfter(dayjs());
  })();
  const scrollToQuestionForm = () => {
    document.getElementById("qa")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // QA一覧
  const renderFAQSection = () => {
    log.debug(`renderFAQSection qaList`,qaList)
    return (
      <s.FaqSection>
        <h3>
          質問合計 {qaCnt}件
          {isQuestion && canAsk && (
            <span style={{ float: "right" }}>
              <s.GrayBtn onClick={scrollToQuestionForm}>新しく質問をする</s.GrayBtn>
            </span>
          )}
        </h3>
        {qaList.length === 0 ? (
          <p>{qaCnt ? "回答済みの" : ""}質問はまだありません。</p>
        ) : (
          <dl>
            {qaList.map((qa) => (
              <React.Fragment key={qa.Q_no}>
                <dt>
                  <span className="que">Q</span>
                  質問{qa.Q_no}　{dayjs(qa.created).format("M月DD日 HH時mm分")}
                </dt>
                <dd>
                  <p>{qa.QAnaiyo}</p>
                  {qa.A && qa.A.length > 0 ? (
                    qa.A.map((answer, index) => (
                      <dl key={index}>
                        <dt>
                          <s.AnsSpan className="ans">A</s.AnsSpan>
                          回答{index + 1}　
                          {dayjs(answer.created).format("M月D日 HH時mm分")}
                        </dt>
                        <dd>{answer.QAnaiyo}</dd>
                      </dl>
                    ))
                  ) : (
                    <p>回答はまだありません。</p>
                  )}
                </dd>
              </React.Fragment>
            ))}
          </dl>
        )}
      </s.FaqSection>
    );
  };

  const isModal = false;
  return (
    <s.Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      hideBackdrop={isModal}
      style={!isModal ? { pointerEvents: "none" } : { pointerEvents: "auto" }}
      BackdropProps={
        !isModal
          ? {
              style: { pointerEvents: "none" }, // 背景は操作不可にする
            }
          : {}
      }
      PaperProps={
        !isModal
          ? {
              style: { pointerEvents: "auto" }, // ダイアログ自体は操作可能にする
            }
          : {}
      }
    >
      { isQuestion ? (
        <>
          {/* 質問 */}
          <s.DialogTitle>
            <s.FlexBetween>
              <s.Title>依頼元へ質問をする</s.Title>
              <IconButton onClick={handleClose} size="small" sx={{ float: "right" }}>
                <CloseIcon />
              </IconButton>
            </s.FlexBetween>
          </s.DialogTitle>

          <s.DialogContent>
            <s.Container>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
              {/* 注文詳細情報 */}
              <s.OrderDetail>
                <s.OrderDetailList>
                  <s.OrderDetailLabel>見積番号</s.OrderDetailLabel>
                  <s.OrderDetailValue>{MitsumoriNo}</s.OrderDetailValue>
                  <s.OrderDetailLabel>車名</s.OrderDetailLabel>
                  <s.OrderDetailValue>{CarName || ""}</s.OrderDetailValue>
                  <s.OrderDetailLabel>車輌型式</s.OrderDetailLabel>
                  <s.OrderDetailValue>{Sharyo_Katashiki || ""}</s.OrderDetailValue>
                  <s.OrderDetailLabel>年式</s.OrderDetailLabel>
                  <s.OrderDetailValue>{Sharyo_Nenshiki || ""}</s.OrderDetailValue>
                  <s.OrderDetailLabel className="order">見積依頼部品</s.OrderDetailLabel>
                  <s.OrderDetailValue>{partsData?.PartsName || ""}</s.OrderDetailValue>
                </s.OrderDetailList>
              </s.OrderDetail>

              {/* QA一覧 */}
              {isShowInit && (
                <>
                  {renderFAQSection()}
                  {/* 入力エリア（掲載期限内のみ表示） */}
                  <s.FaqSection>
                    {isAnswerOpen && canAsk && (
                      <s.AnswerBlock>
                        <h3 id="qa">
                          質問をする
                        </h3>
                        <p>
                          依頼元への質問を入力し「確認する」ボタンを押して下さい。
                          <br />
                          <span style={{ fontSize: "12px", color: "#666" }}>例)「年式、前期後期を教えてください」「色は不問でしょうか」</span>
                        </p>
                        {formProps.input_QAnaiyo(
                          {
                            label: "質問内容を入力してください",
                            fullWidth: true,
                            multiline: true,
                            maxRows: 5,
                            style: { width: "100%" },
                          },
                          true
                        )}
                        {/* <s.Textarea value={answerText} onChange={(e) => setAnswerText(e.target.value)} placeholder="回答内容を入力してください" /> */}
                        ※HTMLタグは使用できません。
                        <s.WarningBox>
                          <ul>
                            <li>
                              <strong>社名/名前などの個人情報</strong>を入力しないようにご注意ください。
                            </li>
                            <li>質問は利用規約に則ったご利用をお願いします。</li>
                            <li>
                              <strong>質問/回答は他の利用者にも公開されます。</strong>
                            </li>
                            <li>送信する前に入力内容をよく確認して下さい。一度掲載された内容は編集/削除できません。</li>
                          </ul>
                        </s.WarningBox>
                        {error && <Alert severity="error">{error}</Alert>}
                        <p className="order-complete" style={{ margin: "10px 0", textAlign: "center" }}>
                          <s.ImportantBtn
                            onClick={async () => {
                              if (!form.QAnaiyo.trim()) {
                                actions.showAlert("質問内容を入力してください");
                                return;
                              }

                              const result = await formProps.confirm();
                              if (!result?.result) {
                                actions.showAlert(result?.error || "確認に失敗しました");
                              }
                            }}
                            className="important-btn"
                            style={{ cursor: "pointer" }}
                          >
                            確認する
                          </s.ImportantBtn>
                        </p>
                      </s.AnswerBlock>
                    )}
                  </s.FaqSection>
                </>
              )}

              {/* 確認画面 */}
              {isShowConfirm && (
                <>
                  <s.ConfirmSection>
                    <h3>確認</h3>
                    {error && <p style={{ color: "red" }}>{error}</p>}

                    <p style={{ whiteSpace: "pre-wrap" }}>{form.QAnaiyo}</p>

                    <div style={{ textAlign: "center", color: "#ff0000" }}>
                      送信する前に入力内容をよく確認して下さい。一度掲載された内容は編集/削除できません。
                      <br />
                      質問/回答は他の利用者にも公開されます。
                    </div>

                    <p style={{ textAlign: "center" }}>
                      <s.ImportantBtn
                        onClick={async () => {
                          if (!form.QAnaiyo.trim()) {
                            actions.showAlert("質問内容を入力してください");
                            return;
                          }

                          //const result = await formProps.submitAnswer();
                          const result = await formProps.submitQuestion();

                          if (result.result) {
                            setError("");
                            formProps.setIsShowInit(false);
                            formProps.setIsAnswerOpen(false);
                            formProps.setIsShowFinish(true);
                            formProps.setIsShowConfirm(false);
                          } else {
                            actions.showAlert(result.error || "質問の送信に失敗しました");
                          }
                        }}
                        className="important-btn"
                        style={{ cursor: "pointer" }}
                      >
                        送信する
                      </s.ImportantBtn>
                      <s.GrayBtn
                        onClick={() => {
                          formProps.setIsShowInit(true);
                          formProps.setIsAnswerOpen(true);
                          formProps.setIsShowConfirm(false);
                          formProps.setIsShowFinish(false);
                        }}
                        className="btn_gray"
                        style={{ marginLeft: "8px" }}
                      >
                        修正する
                      </s.GrayBtn>
                    </p>
                  </s.ConfirmSection>
                  {renderFAQSection()}
                </>
              )}

              {/* 送信完了画面 */}
              {isShowFinish && (
                <s.FinishSection>
                  <h3>質問を完了しました</h3>
                  <p className="order-complete">質問を送信しました。</p>
                  <p className="order-complete">
                    <s.GrayBtn onClick={onClose}>OK</s.GrayBtn>
                  </p>
                </s.FinishSection>
              )}
            </>
              )}
            </s.Container>
          </s.DialogContent>
        </>
      ):(
        <>
          {/* 回答 */}
          <s.DialogTitle>
            <s.FlexBetween>
              {isShowMikaitoList === false && <s.Title>質問への回答を行う</s.Title>}
              {isShowMikaitoList === true && <s.Title>見積依頼先からの質問への回答を行う</s.Title>}
              <IconButton onClick={handleClose} size="small" sx={{ float: "right" }}>
                <CloseIcon />
              </IconButton>
            </s.FlexBetween>
          </s.DialogTitle>

          <s.DialogContent sx={{ p: 3 }}>
            <s.Container>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
              {/* 注文詳細情報 */}
              <s.OrderDetail>
                <s.OrderDetailList>
                  <s.OrderDetailLabel>見積番号</s.OrderDetailLabel>
                  <s.OrderDetailValue>{MitsumoriNo}</s.OrderDetailValue>
                  <s.OrderDetailLabel>車名</s.OrderDetailLabel>
                  <s.OrderDetailValue>{CarName || ""}</s.OrderDetailValue>
                  <s.OrderDetailLabel>車輌型式</s.OrderDetailLabel>
                  <s.OrderDetailValue>{Sharyo_Katashiki || ""}</s.OrderDetailValue>
                  <s.OrderDetailLabel>年式</s.OrderDetailLabel>
                  <s.OrderDetailValue>{Sharyo_Nenshiki || ""}</s.OrderDetailValue>
                  <s.OrderDetailLabel className="order">見積依頼部品</s.OrderDetailLabel>
                  <s.OrderDetailValue>{qaData?.PartsName || ""}</s.OrderDetailValue>
                </s.OrderDetailList>
              </s.OrderDetail>

              {/* 未回答質問一覧 */}
              {(() => {
                if (isShowMikaitoList === false) {
                  return null;
                }
                if (mikaitoList.length === 0) {
                  return (
                    <s.FaqSection>
                      <p>未回答の質問はありません。</p>
                    </s.FaqSection>
                  );
                }
                return (
                  <s.FaqSection>
                    <>
                      <p>回答を行う質問を選択してください。</p>
                      <s.SimpleTable>
                        <thead>
                          <tr>
                            <th style={{ width: "12em" }}>質問日時</th>
                            <th>質問内容</th>
                            <th style={{ width: "8em" }}>質問者地域</th>
                            <th style={{ width: "8em" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {mikaitoList.map((qa) => (
                            <tr key={qa.Q_no}>
                              <td className="date">
                                {(() => {
                                  const date = new Date(qa.created);
                                  const month = date.getMonth() + 1;
                                  const day = date.getDate();
                                  const hour = date.getHours().toString().padStart(2, "0");
                                  const minute = date.getMinutes().toString().padStart(2, "0");
                                  return `${month}月${day}日 ${hour}時${minute}分`;
                                })()}
                              </td>
                              <td className="txt" style={{ wordBreak: "break-all" }}>
                                {qa.QAnaiyo}
                              </td>
                              <td className="locale">{qa.Todofuken}</td>
                              <td className="ctrl">{isAnswerOpen ? <s.GrayBtn onClick={() => handleSelectQa(qa)}>回答する</s.GrayBtn> : "未回答"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </s.SimpleTable>
                    </>
                  </s.FaqSection>
                );
              })()}
              {/* 回答入力エリア */}
              <s.FaqSection>
                {selectedQa && isAnswerOpen && (
                  <s.AnswerBlock>
                    <h3>質問への回答</h3>
                    <dl>
                      <dt>
                        <span className="que">Q</span>
                        <span style={{ marginRight: "8px" }}>質問{selectedQa.Q_no}</span>
                        <span className="date">
                          {(() => {
                            const date = new Date(selectedQa.created);
                            const month = date.getMonth() + 1;
                            const day = date.getDate();
                            const hour = date.getHours();
                            const minute = date.getMinutes().toString().padStart(2, "0");
                            return `${month}月${day}日 ${hour}時${minute}分`;
                          })()}
                        </span>
                        <span className="locale">{selectedQa.Todofuken && `(${selectedQa.Todofuken})`}</span>
                      </dt>
                      <dd>
                        <p className="txt" style={{ wordBreak: "break-all" }}>
                          {selectedQa.QAnaiyo}
                        </p>
                      </dd>
                    </dl>
                    <p>質問への回答を入力し「確認する」ボタンを押して下さい。</p>
                    {formProps.input_QAnaiyo({ fullWidth: true, multiline: true, maxRows: 10, style: { width: "100%" } }, true)}
                    {/* <s.Textarea value={answerText} onChange={(e) => setAnswerText(e.target.value)} placeholder="回答内容を入力してください" /> */}
                    ※HTMLタグは使用できません。
                    <s.WarningBox>
                      <ul>
                        <li>
                          <strong>社名/名前などの個人情報</strong>を入力しないようにご注意ください。
                        </li>
                        <li>質問は利用規約に則ったご利用をお願いします。</li>
                        <li>
                          <strong>質問/回答は他の利用者にも公開されます。</strong>
                        </li>
                        <li>送信する前に入力内容をよく確認して下さい。一度掲載された内容は編集/削除できません。</li>
                      </ul>
                    </s.WarningBox>
                    {error && <Alert severity="error">{error}</Alert>}
                    <p className="order-complete" style={{ margin: "10px 0", textAlign: "center" }}>
                      <s.ImportantBtn onClick={() => formProps.confirm()}>確認する</s.ImportantBtn>
                    </p>
                  </s.AnswerBlock>
                )}
              </s.FaqSection>
              {isShowConfirm && (
                <s.ConfirmSection>
                  <h3>確認</h3>
                  {error && <p style={{ color: "red" }}>{error}</p>}

                  <dl>
                    <dt>
                      <span className="que">Q</span>
                      質問{selectedQa?.Q_no}
                      {selectedQa?.created &&
                        (() => {
                          const date = new Date(selectedQa.created);
                          const month = date.getMonth() + 1;
                          const day = date.getDate();
                          const hour = date.getHours();
                          const minute = date.getMinutes().toString().padStart(2, "0");
                          return ` ${month}月${day}日 ${hour}時${minute}分`;
                        })()}
                      {selectedQa?.Todofuken && ` (${selectedQa.Todofuken})`}
                    </dt>
                    <dd>
                      <p style={{ whiteSpace: "pre-wrap" }}>{selectedQa?.QAnaiyo}</p>
                      <dl>
                        <dt>
                          <span className="ans">A</span> 回答
                        </dt>
                        <dd>
                          <p style={{ whiteSpace: "pre-wrap" }}>{form.QAnaiyo}</p>
                        </dd>
                      </dl>
                    </dd>
                  </dl>

                  <div style={{ textAlign: "center", color: "#ff0000" }}>
                    送信する前に入力内容をよく確認して下さい。一度掲載された内容は編集/削除できません。
                    <br />
                    質問/回答は他の利用者にも公開されます。
                  </div>

                  <p style={{ textAlign: "center" }}>
                    <s.ImportantBtn
                      onClick={async () => {
                        if (!form.QAnaiyo.trim()) {
                          actions.showAlert("回答内容を入力してください");
                          return;
                        }

                        const result = await formProps.submitAnswer();

                        if (result.result) {
                          setError("");
                          formProps.setIsShowMikaitoList(true);
                          formProps.setIsAnswerOpen(false);
                          formProps.setIsShowFinish(true);
                          formProps.setIsShowConfirm(false);
                        } else {
                          actions.showAlert(result.error || "回答の送信に失敗しました");
                        }
                      }}
                      className="important-btn"
                      style={{ cursor: "pointer" }}
                    >
                      送信する
                    </s.ImportantBtn>
                    <s.GrayBtn
                      onClick={() => {
                        formProps.setIsShowMikaitoList(true);
                        formProps.setIsAnswerOpen(true);
                        formProps.setIsShowConfirm(false);
                        formProps.setIsShowFinish(false);
                      }}
                      className="btn_gray"
                      style={{ marginLeft: "8px" }}
                    >
                      修正する
                    </s.GrayBtn>
                  </p>
                </s.ConfirmSection>
              )}

              {isShowFinish && (
                <s.FinishSection>
                  <h3>回答を完了しました</h3>
                  <p className="order-complete">質問を送信しました。</p>
                  <p className="order-complete">
                    <s.GrayBtn onClick={onClose}>OK</s.GrayBtn>
                  </p>
                </s.FinishSection>
              )}

              {/* 回答済み質問一覧 */}
              {renderFAQSection()}
            </>
              )}
            </s.Container>
          </s.DialogContent>
        </>
      )}
    </s.Dialog>
  );
}
